import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { itemList, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/glossary/')({
  component: GlossaryIndexPage,
  head: () => ({
    meta: [
      { title: 'Monitoring Glossary — Key Terms Explained | Uptrack' },
      { name: 'description', content: 'Learn the key concepts behind uptime monitoring, incident management, and site reliability. Plain-English definitions for developers and ops teams.' },
      { property: 'og:title', content: 'Monitoring Glossary — Key Terms Explained | Uptrack' },
      { property: 'og:description', content: 'Plain-English definitions of uptime monitoring, SLA, MTTR, incident management, and more.' },
      { property: 'og:image', content: 'https://uptrack.app/og/glossary.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/glossary' },
    ],
    scripts: [
      jsonLdScript({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Monitoring Glossary',
        description: 'A-Z glossary of uptime monitoring, incident management, and site reliability terms.',
        url: 'https://uptrack.app/glossary',
      }),
      jsonLdScript(itemList(
        'Monitoring Glossary',
        terms.map((t) => ({ name: t.name, url: `https://uptrack.app${t.href}`, description: t.description })),
        'https://uptrack.app/glossary',
      )),
    ],
  }),
})

const terms = [
  { name: 'Alert Fatigue', href: '/glossary/alert-fatigue', description: 'When too many false alerts cause teams to ignore real incidents.' },
  { name: 'DNS Propagation', href: '/glossary/dns-propagation', description: 'How DNS record changes spread across the global network of servers.' },
  { name: 'Downtime', href: '/glossary/downtime', description: 'Any period when a service is unavailable to its users.' },
  { name: 'Error Budget', href: '/glossary/error-budget', description: 'The allowed amount of downtime before an SLA is violated.' },
  { name: 'Escalation Policy', href: '/glossary/escalation-policy', description: 'Rules for escalating unacknowledged incidents to additional responders.' },
  { name: 'Five Nines', href: '/glossary/five-nines', description: '99.999% uptime — just 5.26 minutes of downtime per year.' },
  { name: 'Heartbeat Monitoring', href: '/glossary/heartbeat-monitoring', description: 'Passive monitoring where the service pings the monitor on a schedule.' },
  { name: 'Incident Management', href: '/glossary/incident-management', description: 'The process of identifying, analyzing, and resolving service disruptions.' },
  { name: 'Latency', href: '/glossary/latency', description: 'The time delay between sending a request and receiving a response.' },
  { name: 'MTBF', href: '/glossary/mtbf', description: 'Mean Time Between Failures — average time from one failure to the next.' },
  { name: 'MTTD', href: '/glossary/mttd', description: 'Mean Time To Detect — how long before a failure is noticed.' },
  { name: 'MTTF', href: '/glossary/mttf', description: 'Mean Time To Failure — average operating time before a failure occurs.' },
  { name: 'MTTR', href: '/glossary/mttr', description: 'Mean Time To Repair — average time to restore service after a failure.' },
  { name: 'On-Call', href: '/glossary/on-call', description: 'A rotation system for who responds to incidents outside working hours.' },
  { name: 'Real User Monitoring', href: '/glossary/real-user-monitoring', description: 'Collecting performance data from actual user sessions.' },
  { name: 'SLA', href: '/glossary/sla', description: 'Service Level Agreement defining expected availability and consequences for breaches.' },
  { name: 'SSL Certificate', href: '/glossary/ssl-certificate', description: 'Digital certificate enabling encrypted HTTPS communication.' },
  { name: 'Status Page', href: '/glossary/status-page', description: 'A public page showing the current health and history of your services.' },
  { name: 'Synthetic Monitoring', href: '/glossary/synthetic-monitoring', description: 'Simulating user requests to proactively test service availability.' },
  { name: 'Uptime', href: '/glossary/uptime', description: 'The percentage of time a service is operational and accessible.' },
]

function GlossaryIndexPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-12 md:py-16">
          <p className="mb-4 text-sm text-muted-foreground">20 terms explained</p>
          <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Monitoring Glossary</h1>
          <p className="mt-5 max-w-2xl text-lead text-body">
            Plain-English definitions of uptime monitoring, incident management, and site reliability concepts. Built for developers and ops teams.
          </p>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {terms.map((t) => (
          <Link
            key={t.href}
            to={t.href}
            className="group flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
          >
            <div>
              <h2 className="text-[17px] font-semibold text-foreground group-hover:text-primary">{t.name}</h2>
              <p className="mt-1.5 text-[15px] leading-relaxed text-body">{t.description}</p>
            </div>
            <ArrowRight aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
          </Link>
        ))}
      </div>
        </div>
      </section>

      <section className="border-y border-border bg-card">
        <div className="container mx-auto grid grid-cols-1 items-center gap-6 px-4 py-12 md:py-[72px] lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Start monitoring your sites now</h2>
            <p className="mt-3 max-w-xl text-lead text-body">Every plan includes 30-second checks and alert confirmation.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
            <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>
    </>
  )
}
